import { ArrowLeft, MailWarning, Shield, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { contactsApi } from '@/api/contacts';
import { ContactForm } from '@/components/contacts/ContactForm';
import { ContactStatusBadge } from '@/components/contacts/ContactStatusBadge';
import { ContactVerificationModal } from '@/components/contacts/ContactVerificationModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { ContactWithStatus } from '@/types/contacts';

export function ContactDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState<ContactWithStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [telegramLink, setTelegramLink] = useState<string | null>(null);
  const [resubscribeNotice, setResubscribeNotice] = useState<string | null>(null);
  const [isRequestingResubscribe, setIsRequestingResubscribe] = useState(false);

  const fetchContact = useCallback(async (contactId: number) => {
    try {
      setLoading(true);
      const data = await contactsApi.getById(contactId);
      setContact(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch contact');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchContact(Number.parseInt(id, 10));
    }
  }, [id, fetchContact]);

  const handleUpdate = async (data: {
    name: string;
    type: 'email' | 'phone' | 'telegram';
    email?: string | null;
    phone_number?: string | null;
    telegram_chat_id?: string | null;
  }) => {
    if (!contact) return;

    setIsSubmitting(true);
    try {
      await contactsApi.update(contact.id, data);
      await fetchContact(contact.id);
      setIsEditing(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!contact) return;
    if (!confirm(`Are you sure you want to delete "${contact.name}"?`)) {
      return;
    }

    try {
      await contactsApi.delete(contact.id);
      navigate('/dashboard/contacts');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete contact');
    }
  };

  const handleVerify = async () => {
    if (!contact) return;

    if (contact.type === 'telegram') {
      await handleConnectTelegram();
      return;
    }

    if (contact.verification_status === 'requested') {
      setVerifying(true);
    } else {
      try {
        const result = await contactsApi.requestVerification(contact.id);
        if ('error' in result) {
          setError(result.message);
        } else {
          setVerifying(true);
          await fetchContact(contact.id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to request verification');
      }
    }
  };

  const handleVerificationSubmit = async (code: string) => {
    if (!contact) return;

    const result = await contactsApi.submitVerificationCode(contact.id, code);
    if ('error' in result) {
      throw new Error(result.message);
    }
    setVerifying(false);
    await fetchContact(contact.id);
  };

  const handleResend = async () => {
    if (!contact) return;

    const result = await contactsApi.requestVerification(contact.id);
    if ('error' in result) {
      throw new Error(result.message);
    }
    await fetchContact(contact.id);
  };

  const handleConnectTelegram = async () => {
    if (!contact) return;
    try {
      const result = await contactsApi.requestTelegramLink(contact.id);
      if ('error' in result) {
        setError(result.message);
        return;
      }
      setTelegramLink(result.deepLink);
      window.open(result.deepLink, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request Telegram link');
    }
  };

  const handleRequestMonitoringEmailResubscribe = async () => {
    if (!contact) return;

    setIsRequestingResubscribe(true);
    setResubscribeNotice(null);
    try {
      const result = await contactsApi.requestMonitoringEmailResubscribe(contact.id);
      if ('error' in result) {
        setError(result.message);
        return;
      }
      setContact({ ...contact, monitoringResubscribePending: true });
      setResubscribeNotice('A re-subscription confirmation has been queued for this recipient.');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to request monitoring email re-subscription',
      );
    } finally {
      setIsRequestingResubscribe(false);
    }
  };

  const canVerify = !!(
    contact &&
    contact.verification_status !== 'verified' &&
    !contact.blocked_at
  );
  const canResend = !!(
    contact &&
    (!contact.next_allowed_at || new Date() >= new Date(contact.next_allowed_at))
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading contact...</div>
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="p-6">
        <div className="text-center">
          <p className="text-gray-500">Contact not found</p>
          <Link
            to="/dashboard/contacts"
            className="text-blue-600 hover:text-blue-800 mt-4 inline-block"
          >
            Back to Contacts
          </Link>
        </div>
      </div>
    );
  }

  const getDestination = () => {
    if (contact.type === 'email') return contact.email;
    if (contact.type === 'telegram') return contact.telegram_chat_id;
    return contact.phone_number;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link
          to="/dashboard/contacts"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Contacts
        </Link>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {telegramLink && contact?.type === 'telegram' && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg">
          <div className="font-medium">Telegram link ready</div>
          <div className="text-sm mt-1">
            <a href={telegramLink} className="underline" target="_blank" rel="noreferrer">
              {telegramLink}
            </a>
          </div>
        </div>
      )}

      {resubscribeNotice && (
        <output className="mb-4 block rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-emerald-800">
          {resubscribeNotice}
        </output>
      )}

      <Card className="max-w-2xl">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>{isEditing ? 'Edit Contact' : contact.name}</CardTitle>
              {!isEditing && (
                <div className="mt-2">
                  <ContactStatusBadge status={contact.verification_status} />
                  {contact.blocked_at && <span className="ml-2 text-sm text-red-600">Blocked</span>}
                </div>
              )}
            </div>
            {!isEditing && (
              <div className="flex gap-2">
                {canVerify && (
                  <Button
                    variant="outline"
                    onClick={handleVerify}
                    disabled={contact.type === 'phone' || !canResend}
                    title={
                      contact.type === 'phone'
                        ? 'SMS verification is unavailable'
                        : contact.next_allowed_at && !canResend
                          ? `Available at ${new Date(contact.next_allowed_at).toLocaleTimeString()}`
                          : ''
                    }
                  >
                    <Shield className="h-4 w-4 mr-1" />
                    {contact.type === 'telegram'
                      ? 'Connect Telegram'
                      : contact.verification_status === 'requested'
                        ? 'Enter Code'
                        : 'Verify'}
                  </Button>
                )}
                <Button variant="outline" onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  aria-label={`Delete contact ${contact.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <ContactForm
              contact={contact}
              onSubmit={handleUpdate}
              onCancel={() => setIsEditing(false)}
              isSubmitting={isSubmitting}
            />
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-sm font-medium text-gray-500">Type</div>
                <p className="mt-1 text-gray-900 capitalize">{contact.type}</p>
              </div>

              <div>
                <div className="text-sm font-medium text-gray-500">Destination</div>
                <p className="mt-1 text-gray-900 font-mono">{getDestination() ?? 'Not linked'}</p>
              </div>

              {contact.type === 'email' && contact.monitoringEmailStatus === 'unsubscribed' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <div className="flex gap-2">
                    <MailWarning className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <div>
                      <p className="font-medium">Monitoring emails unsubscribed</p>
                      <p className="mt-1 leading-6">
                        This recipient will not receive outage, reminder, or recovery emails from
                        this organization.
                      </p>
                      {contact.monitoringResubscribePending ? (
                        <p className="mt-3">
                          A re-subscription confirmation is awaiting recipient action.
                        </p>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          className="mt-3 border-amber-300 bg-amber-50"
                          onClick={handleRequestMonitoringEmailResubscribe}
                          disabled={isRequestingResubscribe}
                        >
                          {isRequestingResubscribe
                            ? 'Requesting re-subscription...'
                            : 'Request re-subscription'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {contact.type === 'phone' && (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                  SMS delivery is unavailable. This phone number is saved for future use.
                </div>
              )}

              {contact.type === 'telegram' && contact.telegram_username && (
                <div>
                  <div className="text-sm font-medium text-gray-500">Telegram Username</div>
                  <p className="mt-1 text-gray-900">@{contact.telegram_username}</p>
                </div>
              )}

              {contact.type === 'telegram' && contact.telegram_user_id && (
                <div>
                  <div className="text-sm font-medium text-gray-500">Telegram User ID</div>
                  <p className="mt-1 text-gray-900 font-mono">{contact.telegram_user_id}</p>
                </div>
              )}

              {contact.verification_attempts > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-500">Verification Attempts</div>
                  <p className="mt-1 text-gray-900">{contact.verification_attempts}</p>
                </div>
              )}

              {contact.verified_at && (
                <div>
                  <div className="text-sm font-medium text-gray-500">Verified At</div>
                  <p className="mt-1 text-gray-900">
                    {new Date(contact.verified_at).toLocaleString()}
                  </p>
                </div>
              )}

              {contact.next_allowed_at && !canResend && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-lg">
                  You can resend verification at{' '}
                  {new Date(contact.next_allowed_at).toLocaleTimeString()}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {verifying && contact && (
        <ContactVerificationModal
          contactId={contact.id}
          contactName={contact.name}
          onSubmit={handleVerificationSubmit}
          onCancel={() => setVerifying(false)}
          onResend={handleResend}
          canResend={canResend}
          nextAllowedAt={contact.next_allowed_at ? new Date(contact.next_allowed_at) : undefined}
        />
      )}
    </div>
  );
}
